import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

// Tipos para las respuestas de la API
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
  status: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: unknown;
}

// Configuración base
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3010/api/v1";
const API_TIMEOUT = 10000; // 10 segundos

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Interceptor de requests
    this.client.interceptors.request.use(
      (config) => {
        // Agregar token JWT si existe
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor de responses
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        this.handleError(error);
        return Promise.reject(this.transformError(error));
      }
    );
  }

  private getToken(): string | null {
    // Obtener token del localStorage o donde lo tengas almacenado
    return localStorage.getItem("auth_token");
  }

  private handleError(error: AxiosError): void {
    const status = error.response?.status;

    switch (status) {
      case 401:
        // Token expirado o inválido
        this.handleUnauthorized();
        break;
      case 403:
        // Sin permisos
        break;
      case 404:
        // Recurso no encontrado
        break;
      case 500:
        // Error del servidor
        break;
      default:
        // Error de red
        break;
    }
  }

  private handleUnauthorized(): void {
    // Limpiar token y redirigir al login
    localStorage.removeItem("auth_token");

    // Redirigir al login (puedes usar react-router o window.location)
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  private transformError(error: AxiosError): ApiError {
    const status = error.response?.status || 0;
    const message =
      (error.response?.data as { message?: string })?.message ||
      error.message ||
      "Error desconocido";

    return {
      message,
      status,
      code: error.code,
      details: error.response?.data,
    };
  }

  // Métodos HTTP
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // Método para subir archivos
  async upload<T = unknown>(
    url: string,
    file: File,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });

    return response.data;
  }

  // Método para múltiples archivos
  async uploadMultiple<T = unknown>(
    url: string,
    files: File[],
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });

    return response.data;
  }
}

// Instancia singleton
export const apiClient = new ApiClient();

// Exportar tipos para uso en otros servicios
export type { AxiosRequestConfig, AxiosResponse, AxiosError };
